/* src/components/journal/AddJournalEntryModal.js */
import React, { useRef } from 'react';
import AddJournalEntryForm from './AddJournalEntryForm'; // Import the form

const AddJournalEntryModal = ({ isOpen, onClose, onAddComplete }) => {
  // Ref to access form state/methods if needed, although not strictly necessary here
  // as the form handles its own submission state.
  const formRef = useRef();

  const handleSaveSuccess = () => {
    if (onAddComplete) onAddComplete(); // Call parent's completion handler
    onClose(); // Close the modal on success
  };

  // Trigger form submission from the modal's button
  // We access the form's submit button indirectly via its type="submit"
  // or we could expose a submit method via the ref if needed complex logic.
  const triggerFormSubmit = () => {
    // Find the submit button within the form ref if necessary, or just let the button below trigger it.
    // For simplicity, the button below will trigger the form's onSubmit.
  };

  if (!isOpen) return null;

  return (
    // Modal Overlay
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-30 p-4 animate-fadeIn">
      {/* Modal Content Box - Constrained Width */}
      {/* Applied max-w-2xl to match the journal entry list width */}
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl transform transition-all animate-scaleIn">
        {/* Modal Header */}
        <div className="flex justify-between items-center mb-4 pb-2 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Add New Journal Entry</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl">×</button>
        </div>

        {/* Render the Form Component */}
        {/* Pass the success handler */}
        <AddJournalEntryForm ref={formRef} onSaveSuccess={handleSaveSuccess} />

        {/* Modal Action Buttons */}
        {/* These buttons are outside the form, but the 'Save' button triggers the form's submit */}
        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            // Disable cancel if form is saving (check form's state via ref if needed, but simple check is ok)
            // disabled={formRef.current?.isSaving}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 disabled:opacity-50 text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            // IMPORTANT: This button needs to trigger the form's onSubmit handler.
            // We achieve this by adding a hidden submit button inside the form OR
            // by giving the form an ID and using the `form` attribute on this button.
            type="submit" // This links to the form's onSubmit
            form="add-journal-form" // Assumes the form tag has id="add-journal-form"
            // Disable button if form is saving (check form state via ref if needed)
            // disabled={formRef.current?.isSaving || !formRef.current?.canSubmit}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium shadow-sm transition-colors"
          >
            {/* Check form saving state via ref if needed */}
            {/* {formRef.current?.isSaving ? 'Saving...' : 'Save Entry'} */}
            Save Entry
          </button>
        </div>
      </div>
      {/* Simple animation styles */}
       <style jsx global>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                .animate-fadeIn { animation: fadeIn 0.2s ease-out forwards; }
                @keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
                .animate-scaleIn { animation: scaleIn 0.2s ease-out forwards; }
            `}</style>
    </div>
  );
};

// Add id="add-journal-form" to the form tag in AddJournalEntryForm.js
// Example modification in AddJournalEntryForm.js:
// <form onSubmit={handleSubmit} id="add-journal-form">

export default AddJournalEntryModal;